import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Button, Pagination } from '@aimsos/ui';

import { ActivityGroupList, ActivityQuickFilters } from '../components/activity/index.js';
import { NotePreviewSlideOut } from '../components/notes/index.js';
import DataListFilterBar from '../components/DataListFilterBar.jsx';
import DataListFilterSlideout from '../components/DataListFilterSlideout.jsx';
import EntityListSkeleton from '../components/EntityListSkeleton.jsx';
import { AccessDeniedState, EmptyState, ErrorState } from '../components/states/index.js';
import {
  ACTIVITY_SEARCH_FIELDS,
  ACTIVITY_SORT_DIRECTION,
  ACTIVITY_SORT_FIELD,
  DEFAULT_ACTIVITY_QUICK_FILTER,
} from '../constants/profileTabs.js';
import { HISTORY_EVENT_PARAM } from '../constants/history.js';
import { useActivityFeed } from '../hooks/useActivityFeed.js';
import { useDataListFilters } from '../hooks/useDataListFilters.js';
import { useListPagination, usePaginationLabels } from '../hooks/useListPagination.js';

import { Inbox, SearchX } from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

/**
 * The feed is newest-first, and "Clear all" returns to that rather than to A–Z.
 * Search covers subject / preview / AI summary, not the generic `description`.
 */
const FILTER_DEFAULTS = Object.freeze({
  defaultSortField: ACTIVITY_SORT_FIELD,
  defaultSortDirection: ACTIVITY_SORT_DIRECTION,
  searchFields: ACTIVITY_SEARCH_FIELDS,
});

/**
 * History › Timeline (CT-5724; the Activity tab of CT-5527): a search box, the
 * dynamic filters, the quick-filter chips and the feed grouped by date.
 *
 * It renders under `HistoryTabPage` (kind `TAB_KINDS.ACTIVITY`); the toolbar
 * (New note, Log call, Log meeting, Draft) and its forms belong to
 * `HistoryLayout` and arrive through the outlet context with the subtab switch.
 * When one of those forms saves, `feedVersion` moves and the feed refreshes
 * quietly. `?event=<id>` (CT-5716's "Open in History") highlights that event
 * once and scrolls it into view. It reuses the entity list's filter state and
 * toolbar — one set, not a second that drifts — and the same pinned pager the
 * list pages carry.
 *
 * The CHIP is committed state. Choosing one narrows immediately, returns to page
 * 1 and drops the filter selections made under the previous chip: each chip
 * offers its own filters (they come down with the rows), so a Channel picked
 * under Communications would otherwise narrow Notes by a dimension it does not
 * have. The search text is kept — it is the user's, not the chip's.
 *
 * The eye on a note card (CT-5528) opens its preview here.
 *
 * @param {object} props
 * @param {{ id: string }} props.tab
 */
function ActivityTabPage({ tab }) {
  const { t } = useTranslation('unified-profiles');
  const { entityType, entityId, subtabSwitch, toolbar, feedVersion = 0 } = useOutletContext();

  const [quickFilter, setQuickFilter] = useState(DEFAULT_ACTIVITY_QUICK_FILTER);
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);
  // The note open in the preview, if any.
  const [previewNoteId, setPreviewNoteId] = useState(null);

  const filtersState = useDataListFilters(FILTER_DEFAULTS);
  const { filters, search, orderBy, orderDirection, hasActiveQuery, clearAll, selected, setFilter } =
    filtersState;

  // `?event=<id>` ("Open in History", CT-5716 / CT-5724). A NEW link shows the
  // event under All with nothing narrowing it, and asks the server for the page
  // that holds it — an event on page 3, or hidden by the chip on screen, is not
  // silently missed (Bugbot, #569). "Adjust state while rendering", no effect.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedEvent = searchParams.get(HISTORY_EVENT_PARAM);
  const [handledEvent, setHandledEvent] = useState(null);
  const [focusEventId, setFocusEventId] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  if (requestedEvent && requestedEvent !== handledEvent) {
    setHandledEvent(requestedEvent);
    setFocusEventId(requestedEvent);
    setHighlightedId(null);
    if (quickFilter !== DEFAULT_ACTIVITY_QUICK_FILTER) setQuickFilter(DEFAULT_ACTIVITY_QUICK_FILTER);
    if (hasActiveQuery) clearAll();
  }
  // Once the link has left the URL, the same link again is a new request.
  if (!requestedEvent && handledEvent) setHandledEvent(null);

  const queryKey = `${quickFilter}|${orderBy}|${orderDirection}|${JSON.stringify(filters ?? {})}|${search?.query ?? ''}`;
  const { page, pageSize, resetPage, goToPage, handlePrevPage, handleNextPage, handlePageSizeChange } = useListPagination({
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    queryKey,
  });

  const { items, filterDefs, sortDefs, meta, focus, loading, error, accessDenied, refetch, refresh } = useActivityFeed({
    entityType,
    entityId,
    tabId: tab.id,
    quickFilter,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filters,
    search,
    focusEventId,
  });

  // The server answered for the event: stay on the page it holds, highlight it.
  if (focusEventId && !loading && focus?.eventId === focusEventId) {
    setFocusEventId(null);
    setHighlightedId(focus.found ? focusEventId : null);
    if (meta.currentPage !== page) goToPage(meta.currentPage);
  }

  const { rangeLabel, labels: paginationLabels } = usePaginationLabels({ meta, itemCount: items.length });

  const handleQuickFilterChange = useCallback(
    (next) => {
      if (next === quickFilter) return;
      Object.keys(selected).forEach((id) => setFilter(id, []));
      setQuickFilter(next);
      resetPage();
    },
    [quickFilter, selected, setFilter, resetPage],
  );

  // A note, call or meeting saved from the History toolbar: refresh quietly.
  const lastFeedVersionRef = useRef(feedVersion);
  useEffect(() => {
    if (lastFeedVersionRef.current === feedVersion) return;
    lastFeedVersionRef.current = feedVersion;
    refresh();
  }, [feedVersion, refresh]);

  // The link is answered: drop it from the URL so a later reload does not repeat it.
  useEffect(() => {
    if (!requestedEvent || requestedEvent !== handledEvent || focusEventId) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(HISTORY_EVENT_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [requestedEvent, handledEvent, focusEventId, setSearchParams]);

  const handlePreviewNote = useCallback((item) => setPreviewNoteId(item.id), []);
  const closePreview = useCallback(() => setPreviewNoteId(null), []);

  const openAllFilters = useCallback(() => {
    filtersState.openDraft();
    setIsAllFiltersOpen(true);
  }, [filtersState]);
  const closeAllFilters = useCallback(() => setIsAllFiltersOpen(false), []);

  const results = () => {
    if (loading) return <EntityListSkeleton perPage={pageSize} />;
    if (accessDenied) return <AccessDeniedState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (items.length === 0) {
      return hasActiveQuery ? (
        <EmptyState
          testId="no-results-state"
          icon={SearchX}
          title={t('activity.noResults.title')}
          subtitle={t('activity.noResults.subtitle')}
          action={
            <Button variant="secondary" size="sm" onClick={clearAll}>
              {t('activity.noResults.clear')}
            </Button>
          }
        />
      ) : (
        <EmptyState
          icon={Inbox}
          title={t('activity.empty.title')}
          subtitle={t('activity.empty.subtitle')}
        />
      );
    }

    return <ActivityGroupList items={items} highlightedId={highlightedId} onPreviewNote={handlePreviewNote} />;
  };

  const showPagination = !loading && !error && !accessDenied && meta.totalItems > 0;

  return (
    <div
      data-testid="activity-tab"
      data-quick-filter={quickFilter}
      className={['flex min-w-0 flex-1 flex-col gap-4', showPagination ? '' : 'pb-6'].filter(Boolean).join(' ')}
    >
      {/* Two rows, as in the prototype: the subtab switch, search and All filters
          keep their room; History's actions sit on the row below (CT-5724). */}
      <div data-testid="history-search-row" className="flex shrink-0 flex-wrap items-start gap-3">
        {subtabSwitch}
        <div className="min-w-0 flex-1">
          <DataListFilterBar
            filtersState={filtersState}
            filterDefs={filterDefs}
            sortDefs={sortDefs}
            searchPlaceholder={t('history.timeline.searchPlaceholder')}
            onAllFiltersClick={openAllFilters}
          />
        </div>
      </div>
      {toolbar}

      <ActivityQuickFilters value={quickFilter} onChange={handleQuickFilterChange} />

      <div className="flex min-w-0 flex-1 flex-col">{results()}</div>

      {showPagination ? (
        /* On a WRAPPER, not through `Pagination`'s `className`, which would put
           the padding inside the pill; sticky against the profile's one scroller. */
        <div className="sticky bottom-0 z-10 mt-auto bg-surface-base pb-6">
          <Pagination
            rangeLabel={rangeLabel}
            totalItems={meta.totalItems}
            currentPage={meta.currentPage}
            totalPages={meta.totalPages}
            hasPrevious={meta.hasPrevious}
            hasNext={meta.hasNext}
            perPage={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
            onPageSizeChange={handlePageSizeChange}
            disabled={loading}
            ariaLabelPerPage={t('list.pagination.rowsPerPage')}
            labels={paginationLabels}
          />
        </div>
      ) : null}

      {/* Mounted only while a note is selected: each opening starts clean. A save
          refreshes the list QUIETLY — `refresh` keeps the rows on screen instead
          of flashing the skeleton under an open panel. */}
      {previewNoteId ? (
        <NotePreviewSlideOut
          noteId={previewNoteId}
          entityType={entityType}
          entityId={entityId}
          onClose={closePreview}
          onSaved={refresh}
        />
      ) : null}

      <DataListFilterSlideout
        isOpen={isAllFiltersOpen}
        onClose={closeAllFilters}
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={sortDefs}
      />
    </div>
  );
}

export default ActivityTabPage;
